import React, { useEffect, useRef, useState } from 'react'
import { PopperPlacementType, useMediaQuery } from '@mui/material'
import CustomizableTooltip from '@/components/ui/CustomizableTooltip'

export type TooltipProps = {
  /** The title of the tooltip. */
  title: string
  /** The placement of the tooltip. */
  placement?: PopperPlacementType
  /** The content of the tooltip. */
  children: React.ReactElement
  /** The x offset of the tooltip. */
  offsetX?: number
  /** The y offset of the tooltip. */
  offsetY?: number
  /**
   * When true, the tooltip bubble stays visible regardless of hover/click. Skips the usual hover
   * and touch listeners. Useful for styling/element inspection in Storybook.
   */
  forceOpen?: boolean
  /**
   * When true, the bubble is non-interactive (`pointer-events: none`), so it never captures the
   * cursor. Use when the bubble may overlap its trigger and you don't want it to steal the
   * trigger's hover state.
   */
  disableInteractive?: boolean
}

/** True when the primary input has no hover (e.g. touch devices). */
const useClickInsteadOfHover = (): boolean => useMediaQuery('(hover: none)')

/**
 * Tooltip that shows on hover (desktop) or on click/tap (touch devices).
 * Uses the (hover: none) media query to detect touch-primary devices.
 * On touch, wraps the child in a focusable span so the tooltip can be toggled by tap.
 */
export const Tooltip: React.FC<TooltipProps> = ({
  children,
  offsetX = 0,
  offsetY = 0,
  placement = 'top',
  title,
  forceOpen = false,
  disableInteractive = false,
}) => {
  const [open, setOpen] = useState(false)
  const useClick = useClickInsteadOfHover()
  const triggerRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if (!useClick || !open) return

    // function to determine if the tooltip should be closed
    const handlePointerDown = (e: PointerEvent) => {
      const target = e.target as Node
      const trigger = triggerRef.current
      // If the target is the trigger, don't close the tooltip
      if (trigger?.contains(target)) return
      // If the target is the tooltip, don't close the tooltip
      const tooltipId = trigger?.getAttribute('aria-describedby')
      // If the target is the tooltip, don't close the tooltip
      if (tooltipId && document.getElementById(tooltipId)?.contains(target)) return
      // If the target is not the trigger or tooltip, close the tooltip
      setOpen(false)
    }
    // add the event listener
    document.addEventListener('pointerdown', handlePointerDown)

    // cleanup function to remove the event listener
    return () => document.removeEventListener('pointerdown', handlePointerDown)
  }, [useClick, open])

  if (!title) {
    return children
  }

  const slotProps = {
    popper: {
      modifiers: [{ name: 'offset', options: { offset: [offsetX, offsetY] } }],
    },
    tooltip: {
      className: 'flex items-center min-w-fit !bg-brand-neutral-800 text-2xs font-medium',
      // Forwarded onto MUI's rendered tooltip element so the visible bubble inspects as `Tooltip`.
      'data-ds-component': 'Tooltip',
      'data-ds-placement': placement,
    },
  }

  if (forceOpen) {
    return (
      <CustomizableTooltip
        open={true}
        disableHoverListener
        disableTouchListener
        disableFocusListener
        disableInteractive={disableInteractive}
        placement={placement}
        slotProps={slotProps}
        title={title}
      >
        {children}
      </CustomizableTooltip>
    )
  }

  if (useClick) {
    return (
      <CustomizableTooltip
        disableFocusListener
        disableHoverListener
        disableTouchListener
        disableInteractive={disableInteractive}
        onClose={() => setOpen(false)}
        open={open}
        placement={placement}
        slotProps={slotProps}
        title={title}
      >
        <span
          ref={triggerRef}
          data-ds-component="Tooltip"
          data-ds-placement={placement}
          className="block w-full"
          role="button"
          tabIndex={0}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation()
            setOpen((prev) => !prev)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              e.stopPropagation()
              setOpen((prev) => !prev)
            }
          }}
        >
          {children}
        </span>
      </CustomizableTooltip>
    )
  }

  return (
    <CustomizableTooltip placement={placement} slotProps={slotProps} title={title} disableInteractive={disableInteractive}>
      {children}
    </CustomizableTooltip>
  )
}

export { useClickInsteadOfHover }

export default Tooltip
